import PropTypes from "prop-types";
import React from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

import QuantawindVideo from "../../pages/QwVideo1.mp4";

import cfdWhitepaper from "../../pages/cfd_whitepaper.png";
import vmmWhitepaper from "../../pages/vmm_whitepaper.jpeg";
import msWhitepaper from "../../pages/ms_whitepaper.png";

const productsPageData = [
  {
    name: "Micrositing Optimization",
    text: `The app incorporates advanced features for conducting obstacle and terrain assessments, which are crucial for planning and optimizing site layouts. By complying with IEC61400-2019 standards, QuantaWind provides a reliable framework for identifying potential challenges and making informed decisions regarding site development and turbine placement.`,
  },
  {
    name: "Sensor Correction",
    text: `The app provides robust solutions for identifying and correcting data gaps, a common issue in data collection at both minor and major timescales. This automated process ensures continuous data flow, enhancing analysis accuracy. Details on how QuantaWind approaches these corrections are readily available in the in-app FAQs, offering users insights into the sophisticated algorithms behind the scenes.`,
  },
  {
    name: "Mast Data Analysis",
    text: `The ability to compare wind speed and direction sensors directly within the app streamlines the process of sensor performance evaluation. This feature supports the detailed analysis of sensor data, enabling users to make informed decisions about sensor placement, maintenance, and data reliability.`,
  },
  {
    name: "Long-Term Data Analysis",
    text: `With access to long-term reference data and the ability to employ machine-learning algorithms for data correction and analysis, QuantaWind channels leading technological innovations in data analysis. These features enable users to enhance the accuracy of their long-term projections and optimize their projects based on advanced analytical techniques.`,
  },
  {
    name: "Computational Fluid Dynamics",
    text: `QuantaWind leverages Computational Fluid Dynamics (CFD) to simulate wind flow over both simple and complex terrains. This powerful tool allows for detailed analysis of wind behavior, enabling precise planning and optimization of turbine placement. By visualizing how wind interacts with various topographies, users can identify potential challenges and optimize energy capture and turbine efficiency.`,
  },
];

const whitepapers = [
  {
    href: "/whitepaper/wrf-cfd",
    image: cfdWhitepaper,
    alt: "Thumbnail for Coupled WRF-CFD Model whitepaper",
    title: "Coupled WRF-CFD Model In QuantaWind",
  },
  {
    href: "/whitepaper/vmm",
    image: vmmWhitepaper,
    alt: "Thumbnail for Virtual Met Mast Methodology whitepaper",
    title: "Virtual Met Mast Methodology in QuantaWind",
  },
  {
    href: "/whitepaper/micrositing",
    image: msWhitepaper,
    alt: "Thumbnail for Micrositing Services whitepaper",
    title: "Micrositing Services in QuantaWind",
  },
];

const galleryAlts = [
  "QuantaWind login page screenshot",
  "QuantaWind dashboards interface screenshot",
  "QuantaWind CFD simulation screenshot",
  "QuantaWind contour visualization screenshot",
];

const responsiveConfig = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
  },
};

const WorkDetailsContainer = ({ data }) => {
  if (!data) return null;

  return (
    <div className="section section-padding">
      <div className="container">
        {/* Hero / About */}
        <div className="row pt--100 pb--80">
          <div className="col-lg-4 col-md-6 col-12">
            <div className="work-left work-details" data-aos="fade-up">
              <div className="portfolio-main-info">
                <video
                  src={QuantawindVideo}
                  className="w-100"
                  loop
                  autoPlay
                  muted
                  playsInline
                  preload="metadata"
                  aria-label="QuantaWind product demonstration video"
                />
              </div>
            </div>
          </div>

          <div className="col-lg-7 col-md-6 offset-lg-1 col-12">
            <div className="work-left work-details mt-6">
              <div className="work-main-info">
                <div className="work-content">
                  <h6 className="title" data-aos="fade-up">
                    ABOUT THE PROJECT
                  </h6>

                  <div className="desc mt-8">
                    {data.body.map((value, i) => (
                      <div
                        key={i}
                        className="content mb-5"
                        data-aos="fade-up"
                        dangerouslySetInnerHTML={{ __html: value }}
                      />
                    ))}

                    <a
                      href="https://quantawind.com/landing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary mt-4 mb-5"
                      data-aos="fade-up"
                    >
                      Explore QuantaWind →
                    </a>

                    <div className="features-heading" data-aos="fade-up">
                      <h2>Features Of QuantaWind</h2>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        {productsPageData.map((feature, i) => (
          <div className="row feature-row" key={i}>
            <div className="col-lg-4 col-md-12 col-12">
              <div className="feature-name" data-aos="fade-up">
                <h3>{feature.name}</h3>
              </div>
            </div>
            <div className="col-lg-7 col-md-12 col-12 offset-lg-1">
              <div
                className="feature-desc mt-lg-0 mt-6"
                data-aos="fade-up"
              >
                <p>{feature.text}</p>
              </div>
            </div>
          </div>
        ))}

        {/* Technical Documentation */}
        <div className="tech-docs-section" data-aos="fade-up">
          <h3>Technical Documentation</h3>
          <div className="tech-docs-grid">
            {whitepapers.map((wp, i) => (
              <a
                key={i}
                href={wp.href}
                target="_blank"
                rel="noopener noreferrer"
                className="tech-doc-card"
                aria-label={`Open whitepaper: ${wp.title}`}
              >
                <div className="tech-doc-card__image">
                  <img
                    src={wp.image}
                    alt={wp.alt}
                    loading="lazy"
                  />
                </div>
                <h4 className="tech-doc-card__title">{wp.title}</h4>
              </a>
            ))}
          </div>
        </div>

        {/* Media Gallery */}
        <div className="custom-layout-gallery mt-lg-20 mt-12">
          <div className="row">
            <Carousel
              additionalTransfrom={0}
              arrows
              autoPlaySpeed={3000}
              centerMode={false}
              containerClass="container"
              draggable
              infinite
              keyBoardControl
              minimumTouchDrag={80}
              pauseOnHover
              renderArrowsWhenDisabled={false}
              renderButtonGroupOutside={false}
              renderDotsOutside={false}
              responsive={responsiveConfig}
              shouldResetAutoplay
              showDots
              slidesToSlide={1}
              swipeable
            >
              <video
                src={`${process.env.PUBLIC_URL}/product/cfd.mp4`}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="carousel-video"
                aria-label="Computational Fluid Dynamics simulation preview"
              />
              <video
                src={`${process.env.PUBLIC_URL}/product/contour.mp4`}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="carousel-video"
                aria-label="Contour visualization preview"
              />
              <video
                src={`${process.env.PUBLIC_URL}/product/ms.mp4`}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="carousel-video"
                aria-label="Micrositing simulation preview"
              />
            </Carousel>

            <div className="col-lg-6 col-md-6 col-12">
              <div
                className="thumbnail gallery-thumbnail gallery-thumbnail--top"
                data-aos="fade-up"
              >
                <img
                  className="w-100"
                  src={`${process.env.PUBLIC_URL}/${data.gallery.imageOne}`}
                  alt={galleryAlts[0]}
                  loading="lazy"
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-10">
              <div
                className="thumbnail gallery-thumbnail gallery-thumbnail--top"
                data-aos="fade-up"
              >
                <img
                  className="w-100"
                  src={`${process.env.PUBLIC_URL}/${data.gallery.imageTwo}`}
                  alt={galleryAlts[1]}
                  loading="lazy"
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-10">
              <div className="thumbnail gallery-thumbnail" data-aos="fade-up">
                <img
                  className="w-100"
                  src={`${process.env.PUBLIC_URL}/${data.gallery.imageThree}`}
                  alt={galleryAlts[2]}
                  loading="lazy"
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-12 mt-lg-0 mt-md-0 mt-10">
              <div className="thumbnail gallery-thumbnail" data-aos="fade-up">
                <img
                  className="w-100"
                  src={`${process.env.PUBLIC_URL}/${data.gallery.imageFour}`}
                  alt={galleryAlts[3]}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

WorkDetailsContainer.propTypes = {
  data: PropTypes.object,
};

export default WorkDetailsContainer;
